import React, { useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { Form, Formik, FormikHelpers } from 'formik';
import Alert from '@material-ui/lab/Alert';

import { unsubscribe } from 'api/newsletter';
import ButtonCustom from 'components/Common/ButtonCustom/ButtonCustom';
import RequiredFields from 'components/Common/RequiredFields/RequiredFields';
import TextFieldCustom from 'components/Common/Formik/TextFieldCustom/TextFieldCustom';
import CheckboxFieldCustom from 'components/Common/Formik/CheckboxFieldCustom/CheckboxFieldCustom';
import { ExtraStyleType } from 'components/Common/Formik/FieldCustom/FieldCustom';
import ScrollToFieldError from 'components/Common/Formik/ScrollToFieldError/ScrollToFieldError';
import FormikErrorsGtmLogger from 'components/Common/FormikErrorsGtmLogger/FormikErrorsGtmLogger';
import Loader from 'components/Common/Loader/Loader';
import Loading from 'components/Common/Loading/Loading';
import TurnstileFormikRow, {
  TurnstileRef,
} from 'components/Common/Cloudflare/TurnstileFormikRow';
import useNewsletterUnsubscribe, {
  IniatialValuesType,
} from 'hooks/useNewsletterUnsubscribe';
import useGtmPageView from 'hooks/gtm/useGtmPageView';
import validationSchema from 'schemas/NewsletterUnsubscribeValidationSchema';
import { apiUrls } from 'services/api-urls';
import { fetcherWithBody } from 'services/fetcher';
import { GTM_PAGE_OTHER } from 'settings/gtm/gtmPageView';
import { GTM_FORM_NEWSLETTER_UNSUBSCRIBE } from 'settings/gtm/gtmForm';
import { CustomerExistsType } from 'types/Customer';
import { useCustomer } from 'hooks/useCustomer';

import styles from './NewsletterUnsubscribePage.module.scss';

export default function NewsletterUnsubscribePage(): JSX.Element {
  const { t } = useTranslation(['newsletterPage', 'translation']);
  const turnstile = useRef<TurnstileRef>();
  const isTurnstileDesktop = useMediaQuery({ minWidth: '402px' });
  const [isLoading, setLoading] = useState(false);
  const [errorMessages, setErrorMessages] = useState<
    Record<string, string | React.ReactElement>
  >();
  const { initialValues } = useNewsletterUnsubscribe();
  const [submitStatus, setSubmitStatus] = useState<boolean | undefined>(
    undefined
  );
  const { isLogged } = useCustomer();
  const [accountNotExists, setAccountNotExists] = useState<boolean>(false);
  const extraStyle: ExtraStyleType = {
    fieldContainer: styles.fieldContainer,
    label: styles.fieldLabel,
  };

  const checkboxExtraStyle: ExtraStyleType = {
    container: styles.checkboxContainer,
    label: styles.checkboxFieldLabel,
  };

  useGtmPageView(
    GTM_PAGE_OTHER,
    `Afibel - ${t('NEWSLETTER_UNSUB_META_TITLE')}`
  );

  async function onSubmitUnsubscribe(
    values: IniatialValuesType,
    actions: FormikHelpers<IniatialValuesType>
  ) {
    setLoading(true);
    setErrorMessages(undefined);

    try {
      const isExistResponse = (await fetcherWithBody(
        apiUrls.getCustomerExists(),
        {
          email: values.email,
          checkPassword: false,
        }
      )) as CustomerExistsType;

      if (!isExistResponse.exists) {
        actions.resetForm();
        setAccountNotExists(true);

        return;
      }

      const response = await unsubscribe({
        turnstileToken: values.turnstileToken,
        email: values.email,
        mailOption: values.mailOption ? false : undefined,
        smsOption: values.smsOption ? false : undefined,
        partOption: values.partOption ? false : undefined,
      });

      if (response.ok) {
        setAccountNotExists(false);
        setSubmitStatus(true);

        return;
      }

      const json = await response.json();

      setErrorMessages(json.error ? { global: json.error } : json.errors || {});
    } finally {
      setLoading(false);
    }
  }

  if (isLogged || isLogged === null) {
    return (
      <>
        <RenderMetadata />
        <Loader />
      </>
    );
  }

  return (
    <div className={styles.container}>
      <RenderMetadata />
      <h1>{t('LABEL_TITLE')}</h1>
      {submitStatus && (
        <Alert severity="success" className={styles.alert}>
          {t('LABEL_YOU_UNSUBSCRIBE')}
        </Alert>
      )}
      {accountNotExists && (
        <Alert severity="warning" className={styles.alert}>
          {t('LABEL_ACCOUNT_NOT_EXISTS')}
        </Alert>
      )}
      <div className={styles.unsubscribe}>
        <div className={styles.header}>
          <h2>{`${t('LABEL_I_UNSUBSCRIBE')} -`}</h2>
          <span>{t('LABEL_ENTER_EMAIL')}</span>
        </div>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema(t)}
          onSubmit={onSubmitUnsubscribe}
          enableReinitialize={true}
        >
          <Form>
            <FormikErrorsGtmLogger
              page_type={GTM_PAGE_OTHER}
              form_type={GTM_FORM_NEWSLETTER_UNSUBSCRIBE}
            />
            <div className={styles.fieldsContainer}>
              <TextFieldCustom
                label={t('LABEL_EMAIL_ADDRESS', { ns: 'translation' })}
                name="email"
                required={true}
                extraStyle={extraStyle}
              />
              <CheckboxFieldCustom
                label={t('LABEL_OFFERS_GENERAL')}
                name="mailOption"
                extraStyle={checkboxExtraStyle}
              />
              <CheckboxFieldCustom
                label={t('LABEL_OFFERS_SMS')}
                name="smsOption"
                extraStyle={checkboxExtraStyle}
              />
              <CheckboxFieldCustom
                label={t('LABEL_OFFERS_PARTNERS')}
                name="partOption"
                extraStyle={checkboxExtraStyle}
              />
              <TurnstileFormikRow
                classes={{
                  row: styles.captchaRow,
                  errorMessage: styles.captchaErrorMessage,
                }}
                turnstileRef={turnstile}
                turnstileProps={{
                  className: styles.captchaField,
                  options: {
                    action: 'newsletter_unsubscribe',
                    size: isTurnstileDesktop ? 'normal' : 'compact',
                  },
                }}
              />
            </div>
            {Object.keys(errorMessages || {}).length > 0 && (
              <div className={styles.formErrorContainer}>
                {Object.values(errorMessages || {}).map((errorMessage, key) => (
                  <div className={styles.formError} key={key}>
                    {errorMessage}
                  </div>
                ))}
              </div>
            )}
            <div className={styles.actionToolbar}>
              <ButtonCustom label={t('LABEL_I_UNSUBSCRIBE')} color="red" />
            </div>
            <RequiredFields />
            <ScrollToFieldError />
          </Form>
        </Formik>
      </div>
      {isLoading && <Loading loading />}
    </div>
  );
}

function RenderMetadata(): JSX.Element {
  const { t } = useTranslation(['newsletterPage']);

  return (
    <Helmet>
      <title>
        {t('META_TITLE_TEMPLATE', {
          title: t('NEWSLETTER_UNSUB_META_TITLE'),
          ns: 'translation',
        })}
      </title>
      <meta
        name="description"
        content={t('NEWSLETTER_UNSUB_META_DESCRIPTION')}
      />
    </Helmet>
  );
}
